import * as React from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ProfileHeader } from '../components/ProfileHeader';
import { ProfileTabs, type AccountTabId } from '../components/ProfileTabs';
import { CreateBanner } from '../components/CreateBanner';
import { InfoGrid } from '../components/InfoGrid';
import { SettingsTab } from '../components/settings/SettingsTab';
import { PaymentsTab } from '../components/payments/PaymentsTab';
import { DeveloperTab } from '../components/developer/DeveloperTab';
import { CreatorEarningsTab } from '../components/earnings/CreatorEarningsTab';
import waveContactListSvg from '@/assets/images/svg/wave_element_contact_list.svg';
import { useSearchParams } from 'react-router-dom';
import { useEchoStore } from '@/context/EchoContext';
import { StatsRow } from '@/features/my-echo/components/StatsRow';
import { useAuth } from '@/hooks';

/**
 * AccountPage Component
 *
 * Main dashboard for user account management.
 * Displays profile information, billing history, and application settings.
 * Uses a tabbed interface for navigation within the account context.
 */
export const AccountPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { myEchoes } = useEchoStore();
  const canUseDeveloperTools = Boolean(user?.is_developer || user?.role === 'admin');
  const canViewEarnings = Boolean(
    user?.role === 'echo' || user?.role === 'creator' || (myEchoes?.length ?? 0) > 0
  );
  const requestedTab = searchParams.get('tab');
  const initialTab: AccountTabId =
    requestedTab === 'profile' ||
    requestedTab === 'billing' ||
    (requestedTab === 'earnings' && canViewEarnings) ||
    requestedTab === 'settings' ||
    (requestedTab === 'developer' && canUseDeveloperTools)
      ? requestedTab
      : 'profile';
  const [activeTab, setActiveTab] = React.useState<AccountTabId>(initialTab);

  // Echo creator context - display stats for primary echo if present
  const primaryEcho = myEchoes?.[0];

  // Sync state if URL params change
  React.useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'developer' && !canUseDeveloperTools) {
      setActiveTab('profile');
      return;
    }
    if (tab === 'earnings' && !canViewEarnings) {
      setActiveTab('profile');
      return;
    }
    if (
      tab === 'profile' ||
      tab === 'billing' ||
      (tab === 'earnings' && canViewEarnings) ||
      tab === 'settings' ||
      (tab === 'developer' && canUseDeveloperTools)
    ) {
      setActiveTab(tab);
    }
  }, [canUseDeveloperTools, canViewEarnings, searchParams]);

  const handleTabChange = (tab: AccountTabId) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    if (tab !== 'billing') {
      next.delete('sub');
      next.delete('focus');
    }
    setSearchParams(next);
  };

  return (
    <div className='relative size-full overflow-hidden bg-white'>
      <div className='pointer-events-none absolute inset-0 bg-account-page-gradient' />

      {/*
        Bottom Left Wave Decoration
        - pointer-events-none: Allows clicking through to elements behind if needed
        - absolute bottom-0 left-0: Pins it to the viewport corner
        - z-0: Keeps it behind the scrollable content
      */}
      <img
        src={waveContactListSvg}
        alt=''
        className='pointer-events-none absolute bottom-0 left-0 z-0 select-none'
      />

      <div className='custom-scrollbar relative z-10 size-full overflow-y-auto p-6 md:p-10'>
        {/* 
           Header: Dark Theme (Black Icons for Desktop) 
           Mobile icons are handled by MainLayout
        */}
        <PageHeader title='ACCOUNT' titleClassName='text-brand-800' theme='dark' />

        <div className='flex w-full flex-col'>
          <div className='w-full max-w-create-banner'>
            <ProfileHeader />
            <ProfileTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              showEarningsTab={canViewEarnings}
              showDeveloperTab={canUseDeveloperTools}
            />
          </div>

          <div className='animate-slide-in flex w-full flex-col'>
            {activeTab === 'profile' && (
              <>
                {primaryEcho && (
                  <div className='mx-auto mb-8 w-full md:mx-0 md:w-3/4'>
                    <StatsRow echo={primaryEcho} />
                  </div>
                )}
                <CreateBanner />
                <InfoGrid />
              </>
            )}

            {activeTab === 'billing' && <PaymentsTab />}

            {activeTab === 'earnings' && canViewEarnings && <CreatorEarningsTab />}

            {activeTab === 'settings' && <SettingsTab />}

            {activeTab === 'developer' && canUseDeveloperTools && <DeveloperTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
